import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { type PartnerTreeNodeV2 } from '@sr/types';
import { partnerTreeSearch } from 'core/lib/utils/partnerTreeSearch';
import { getApiUrl } from '#lib/utils/environment';
import { WithCustomerPortalApi } from '../customerPortalApi';
import { useFlag } from './useFlag';

type RnpExtendedAccessResponse = {
  dwKeys: string[];
};

type UseHasExtendedRnpSiteAccessProps = {
  locations: PartnerTreeNodeV2[];
  selectedLocations?: string[];
};

type UseHasExtendedRnpSiteAccessResult = {
  hasRNPExtendedAccess: boolean;
  selectedLocationHasExtendedRnpAccess: boolean;
  rnpPartnerTrees: PartnerTreeNodeV2[];
};

const STALE_TIME = 10 * 60 * 1000;

export const useHasExtendedRnpSiteAccess = WithCustomerPortalApi<
  RnpExtendedAccessResponse,
  UseHasExtendedRnpSiteAccessResult,
  UseHasExtendedRnpSiteAccessProps
>(
  (fetchFn, { locations, selectedLocations }) => {
    const hasExtendedRnpSiteAccessFlag = useFlag('rnp-extended-site-access');

    const { data } = useQuery({
      queryKey: ['rnp-extended-pilot-access'],
      enabled: hasExtendedRnpSiteAccessFlag,
      staleTime: STALE_TIME,
      queryFn: () =>
        fetchFn({
          url: `${getApiUrl()}/v2/partners/rnp-extended-pilot-access`,
          options: { method: 'GET' },
        }),
    });

    const rnpPartnerTrees = useMemo(() => {
      const accessDwKeys = new Set(data?.dwKeys ?? []);

      if (accessDwKeys.size === 0) {
        return [];
      }

      return locations.filter((tree) =>
        partnerTreeSearch(tree, (item) => accessDwKeys.has(item.dwKey)),
      );
    }, [data, locations]);

    const hasRNPExtendedAccess = rnpPartnerTrees.length > 0;

    let selectedLocationHasExtendedRnpAccess = false;

    if (selectedLocations && selectedLocations.length > 0) {
      selectedLocationHasExtendedRnpAccess = partnerTreeSearch(rnpPartnerTrees, (item) =>
        selectedLocations.includes(item.dwKey),
      );
    }

    return { hasRNPExtendedAccess, selectedLocationHasExtendedRnpAccess, rnpPartnerTrees };
  },
  { hookName: 'useHasExtendedRnpSiteAccess' },
);
